import React from "react";
import { useEffect, useRef, useState } from "react";
import SideMenu from "../General/SideMenu";
import { useDispatch, useSelector } from "react-redux";
import { Show_Message } from "../General/Messagedialogbox";
import { API_POST, DecryptData } from "../General/Utility";
import ReportTable, {
  FinYear_String,
  Is_ValidResponse,
  Parse_Json,
  Sort_Design,
  Today_String,
  Norm_Date,
  Normalize_ReportQuery,
  To_DateInput,
} from "./ReportTable";

// Reports whose script takes the ledger / date range (RB_Acc_* procedures; 1050 = Trial Balance)
const Is_LedgerDateReport = (RptId) => [1015, 1016, 1017, 1018, 1050, 133].indexOf(Number(RptId)) >= 0;
// Accounting statements: fixed layout (running balance / Dr | Cr sides) - never re-sorted, never column-filtered
const Is_StatementReport = (RptId) => [1015, 1016, 1017, 1018, 1050].indexOf(Number(RptId)) >= 0;
// General Ledger shows ONE ledger: nothing is fetched until a ledger is chosen
const Is_LedgerRequired = (RptId) => Number(RptId) === 1015;
const Show_Date = (strIso) => (strIso ? strIso.split("-").reverse().join("-") : "");
// Sort column / order travel in the query string (the request model is unchanged)
const Body_Of = (query) => Normalize_ReportQuery(query); // shared rule (ReportTable.js) for every report request
const Sort_Query = (query) =>
  query && query.strSortCol
    ? `?strSortCol=${encodeURIComponent(query.strSortCol)}&strSortOrder=${query.strSortOrder === "desc" ? "desc" : "asc"}`
    : "";
const BRANCH_LOCK_TEXT = "All Branches (Consolidated)";

function AccountsReport() {
  const dispatch = useDispatch();
  const _AuthParems = useSelector((state) => state.Common.Admin);
  const [iRptId, setiRptId] = useState(0);
  const [iTotalRows, setiTotalRows] = useState(0);
  const [RowPerPage, setRowPerPage] = useState(0);
  const [CurrPage, setCurrPage] = useState(1);
  const [strFromDate, setstrFromDate] = useState(FinYear_String(""));
  const [strLedger, setstrLedger] = useState("");
  const [strToDate, setstrToDate] = useState(Today_String());

  const [strReportName, setstrReportName] = useState("Accounts Reports");
  const [strFilterColName, setstrFilterColName] = useState("");

  const [IsLedgOption, setIsLedgOption] = useState(false);

  const [arrLedger, setarrLedger] = useState([]);
  const [arrLedgerOption, setarrLedgerOption] = useState([]);
  const [arrReports, setarrReports] = useState([]);
  const [arrReportDesing, setarrReportDesing] = useState([]);
  const [arrReportData, setarrReportData] = useState([]);
  const [arrSummarydata, setarrSummarydata] = useState([]);

  const [iBranchId, setiBranchId] = useState(0);
  const [arrBranch, setarrBranch] = useState([]);
  const [arrWH, setarrWH] = useState([]);

  const [IsDateFilter, setIsDateFilter] = useState(false);
  const [IsSortable, setIsSortable] = useState(false);
  const [IsLoading, setIsLoading] = useState(false);
  const [iActiveFilters, setiActiveFilters] = useState(0);
  const [arrAppliedFilters, setarrAppliedFilters] = useState([]);
  const [arrActiveFilters, setarrActiveFilters] = useState([]); // removable filter chips
  const [strFinFromDate, setstrFinFromDate] = useState("");
  const [strBooksFrom, setstrBooksFrom] = useState(""); // FY start from General Settings ("" = not set: no clamping)
  const [IsBranchWise, setIsBranchWise] = useState(false); // rb_tbl_accreportconfig.BranchWiseAccounts
  const [IsPaged, setIsPaged] = useState(false);
  const [strSortCol, setstrSortCol] = useState("");
  const [strSortOrder, setstrSortOrder] = useState("asc");

  const IsMounted = useRef(true);
  const IsInitialised = useRef(false);
  const DateTimer = useRef(null); // header dates: the report is fetched 300ms after the last change
  const QueryRef = useRef(null);
  const ReqSeq = useRef(0);
  const TotalRef = useRef(-1); // total rows of the result on screen (for page-only requests)
  const ListSeq = useRef(0);
  const LedgerSeq = useRef(0);
  // values read by timers / async code (never a stale closure)
  const IsDateFilterRef = useRef(false);
  const BooksFromRef = useRef("");
  const IsBranchWiseRef = useRef(false);
  IsDateFilterRef.current = IsDateFilter;
  BooksFromRef.current = strBooksFrom;
  IsBranchWiseRef.current = IsBranchWise;

  // Consolidated books (default): statements always cover every branch -> Branch box locked
  const IsBranchLocked = Is_StatementReport(iRptId) && !IsBranchWise;
  // ledger list per branch only for branch-wise books; consolidated -> every branch (0)
  const Ledger_BranchId = (BranchId) => (IsBranchWiseRef.current ? Number(BranchId) || 0 : 0);

  useEffect(() => {
    IsMounted.current = true;
    Load_Reports("");
    return () => {
      IsMounted.current = false;
      clearTimeout(DateTimer.current);
    };
  }, []);
  const Load_Reports = async (strtxt) => {
    const iSeq = ++ListSeq.current;
    try {
      const _LocalParems = await DecryptData(localStorage.jbctrlparams);
      const parems = {
        iScrId: 0,
        iPartyId: 0,
        strColName: "AccountsReport",
        strSearch: strtxt || "",
        strDBName: _LocalParems.DB,
        iUserId: Number(_LocalParems.User),
        iClientId: Number(_LocalParems.Client),
        iRoleId: Number(_LocalParems.Role),
        iBranchId: Number(_LocalParems.Branch),
      };
      const res = await API_POST(`Reports/Load_Reports/`, parems, dispatch);
      if (!IsMounted.current || iSeq !== ListSeq.current) return;
      if (Is_ValidResponse(res, dispatch)) {
        setarrReports(Array.isArray(res.objEntRpt) ? res.objEntRpt : []);
        if (!IsInitialised.current) {
          IsInitialised.current = true;
          setiBranchId(Number(_LocalParems.Branch));
          setarrBranch(Array.isArray(res.EntBranch) ? res.EntBranch : []);
          setarrWH(Array.isArray(res.EntWareHouse) ? res.EntWareHouse : []);
          setstrFromDate(FinYear_String(res.strFinFromDate));
          setstrFinFromDate(FinYear_String(res.strFinFromDate));
          const strBooks = Norm_Date(To_DateInput(res.strFinFromDate));
          setstrBooksFrom(strBooks);
          BooksFromRef.current = strBooks;
          setIsBranchWise(Boolean(res.IsBranchWiseAccounts));
          IsBranchWiseRef.current = Boolean(res.IsBranchWiseAccounts);
        }
      }
    } catch (e) {
      if (IsMounted.current) Show_Message(dispatch, e.message, "error");
    }
  };

  const Clear_Data = () => {
    TotalRef.current = -1;
    setIsLoading(false); // a request that is superseded without a new one must not leave "Loading"
    setarrReportData([]);
    setarrSummarydata([]);
    setiTotalRows(0);
    setRowPerPage(0);
    setCurrPage(1);
    setIsPaged(false);
  };
  const Fetch_Report = async (query, iKnownTotal) => {
    QueryRef.current = query;
    const iSeq = ++ReqSeq.current;
    setIsLoading(true);
    try {
      const parems = {
        ...Body_Of(query),
        iWHId: 0, // warehouse is never used by accounts reports
        strDBName: _AuthParems.DB,
        iUserId: Number(_AuthParems.User),
      };
      // page change with unchanged filters: send the known total so the server skips count + totals
      const IsPageOnly = Number.isInteger(iKnownTotal) && iKnownTotal >= 0;
      if (!IsPageOnly) TotalRef.current = -1; // filters changed: the old total is no longer valid
      const strQs = Sort_Query(query);
      const strUrl = IsPageOnly ? `${strQs || "?"}${strQs ? "&" : ""}iKnownTotal=${iKnownTotal}` : strQs;
      const res = await API_POST(`Reports/Load_AccountsReport/${strUrl}`, parems, dispatch);
      if (!IsMounted.current || iSeq !== ReqSeq.current) return;
      if (!Is_ValidResponse(res, dispatch)) {
        Clear_Data();
        return;
      }
      const RptData = Parse_Json(res.EntReport);
      setarrReportDesing(Sort_Design(res.EntDesign));
      setarrReportData(RptData);
      if (!res.IsPageOnly) {
        setarrSummarydata(Parse_Json(res.EntSummary)); // page-only: keep the grand totals
        setarrActiveFilters(Array.isArray(res.EntActive) ? res.EntActive : []);
      }
      setstrFilterColName(res.strSearchCol || "");
      setiTotalRows(Number(res.TotalRows) || 0);
      TotalRef.current = Number(res.TotalRows) || 0;
      setRowPerPage(Number(res.RowPerPage) || 0);
      const iPage = Number(res.CurrPage) || 1;
      setCurrPage(iPage);
      // the server moved the From date to the start of the books: show it and keep the pickers in step
      const strFromUsed = Norm_Date(res.dpFromUsed);
      QueryRef.current = { ...query, CurrtPage: iPage, dpFrom: strFromUsed || query.dpFrom };
      if (res.strNotice) {
        Show_Message(dispatch, res.strNotice, "info");
        if (strFromUsed) setstrFromDate(strFromUsed);
      }
      setIsPaged(Boolean(res.IsPaged));
      setIsSortable(Boolean(res.IsSortable));
      setIsDateFilter(Boolean(res.IsDateFilter));
      setiActiveFilters(Number(res.iActiveFilters) || 0);
      setarrAppliedFilters(Array.isArray(res.EntAppliedFilters) ? res.EntAppliedFilters : []);
    } catch (e) {
      if (IsMounted.current && iSeq === ReqSeq.current) Show_Message(dispatch, e.message, "error");
    } finally {
      if (IsMounted.current && iSeq === ReqSeq.current) setIsLoading(false);
    }
  };
  // a report is fetched only when it can produce a meaningful result:
  //   report selected, ledger chosen for the General Ledger, both dates valid with From <= To for date reports
  const Can_Fetch = (query) => {
    if (!query || !Number(query.iRptId)) return false;
    if (Is_LedgerRequired(query.iRptId) && !String(query.strLedger || "").trim()) return false;
    if (Is_LedgerDateReport(query.iRptId)) {
      const strF = Norm_Date(query.dpFrom);
      const strT = Norm_Date(query.dpTo);
      if (!strF || !strT || strF > strT) return false;
    }
    return true;
  };
  const Refetch = (changes, iKnownTotal) => {
    const query = { ...(QueryRef.current || {}), ...changes };
    QueryRef.current = query;
    if (Can_Fetch(query)) Fetch_Report(query, iKnownTotal);
  };

  const View_Report = async (iId, strName, Branch, WH, LedgerName, Fromdate, ToDate) => {
    const RptId = Number(iId);
    ReqSeq.current++;
    setIsLoading(false);
    setstrReportName(strName);
    setiRptId(RptId);
    setstrSortCol("");
    setstrSortOrder("asc");
    setarrReportDesing([]);
    Clear_Data();
    // nothing of the previous report survives: filter badge, applied list, chips
    setiActiveFilters(0);
    setarrAppliedFilters([]);
    setarrActiveFilters([]);
    setIsDateFilter(Is_LedgerDateReport(RptId));
    const query = {
      iRptId: RptId,
      iPartyId: 0,
      strColName: "",
      strLedger: LedgerName || "",
      strSearch: "",
      iBranchId: Number(Branch) || 0,
      iWHId: 0,
      CurrtPage: 1,
      dpFrom: Fromdate || strFromDate,
      dpTo: ToDate || strToDate,
      strSortCol: "",
      strSortOrder: "asc",
    };
    if (!Can_Fetch(query)) {
      QueryRef.current = query; // General Ledger: waits for a ledger (ReportTable shows "Select a ledger")
      return;
    }
    await Fetch_Report(query);
  };
  const View_PageReport = (Page) => Refetch({ CurrtPage: Number(Page) || 1 }, TotalRef.current);
  const Search_Report = (strtxt, strFilterCol) =>
    Refetch({ strSearch: strtxt || "", strColName: strFilterCol || "", CurrtPage: 1 });
  const Sort_Report = (strCol) => {
    const query = QueryRef.current;
    if (!Can_Fetch(query) || Is_StatementReport(query.iRptId)) return;
    const strOrder = query.strSortCol === strCol && query.strSortOrder === "asc" ? "desc" : "asc";
    setstrSortCol(strCol);
    setstrSortOrder(strOrder);
    Refetch({ strSortCol: strCol, strSortOrder: strOrder, CurrtPage: 1 });
  };
  // Date matrix (validated 300 ms after the last change):
  //   incomplete / invalid date -> keep the current data, no request
  //   From after To             -> message, no request
  //   From before the books     -> moved to the books start + message (Tally rule; the server checks it again)
  //   To before the books       -> message, no request
  //   To in the future          -> allowed (post-dated entries / projections)
  const Date_Change = (strFrom, strTo) => {
    setstrFromDate(strFrom);
    setstrToDate(strTo);
    clearTimeout(DateTimer.current);
    DateTimer.current = setTimeout(() => {
      if (!IsMounted.current) return;
      let strF = Norm_Date(strFrom);
      const strT = Norm_Date(strTo);
      if (!strF || !strT) return; // a date is still being typed / incomplete: keep the current data
      const strBooks = BooksFromRef.current;
      if (strBooks && strT < strBooks) {
        Show_Message(dispatch, `To date cannot be before the start of the books (${Show_Date(strBooks)})`, "info");
        return;
      }
      if (strBooks && strF < strBooks) {
        strF = strBooks;
        setstrFromDate(strBooks);
        Show_Message(dispatch, `From date was set to the start of the books (${Show_Date(strBooks)})`, "info");
      }
      if (strF > strT) {
        Show_Message(dispatch, "From date cannot be after To date", "info");
        return;
      }
      if (QueryRef.current) QueryRef.current = { ...QueryRef.current, dpFrom: strF, dpTo: strT };
      if (IsDateFilterRef.current) Refetch({ dpFrom: strF, dpTo: strT, CurrtPage: 1 });
    }, 300);
  };
  const Ledger_Change = (strLedName) => {
    setstrLedger(strLedName);
    Refetch({ strLedger: strLedName || "", CurrtPage: 1 });
  };
  const Reload_Report = () => {
    if (Can_Fetch(QueryRef.current)) Fetch_Report({ ...QueryRef.current });
  };
  const Download_Report = async () => {
    const query = QueryRef.current;
    if (!Can_Fetch(query)) return null;
    try {
      const res = await API_POST(
        `Reports/Download_AccountsReport/${Sort_Query(query)}`,
        { ...Body_Of(query), iWHId: 0, strDBName: _AuthParems.DB, iUserId: Number(_AuthParems.User) },
        dispatch
      );
      if (!Is_ValidResponse(res, dispatch)) return null;
      return {
        Data: Parse_Json(res.EntReport),
        Summary: Parse_Json(res.EntSummary),
        Design: Sort_Design(res.EntDesign),
      };
    } catch (e) {
      Show_Message(dispatch, e.message, "error");
      return null;
    }
  };
  const Clear_Report = () => {
    // on report change: ledger belongs to report 1015 only; the chosen dates are kept
    setstrLedger("");
    setstrFilterColName("");
    setIsLedgOption(false);
    setarrLedger([]);
    setarrLedgerOption([]);
    setarrReportData([]);
  };
  const Load_Ledger = async (BranchId) => {
    const iSeq = ++LedgerSeq.current;
    try {
      const objSearch = {
        iScrId: 0,
        iPartyId: 0,
        strColName: "Ledger",
        strSearch: "",
        strDBName: _AuthParems.DB,
        iUserId: Number(_AuthParems.User),
        iBranchId: Ledger_BranchId(BranchId !== undefined ? BranchId : iBranchId),
      };
      const res = await API_POST("Reports/Load_Reports/", objSearch, dispatch);
      if (!IsMounted.current || iSeq !== LedgerSeq.current) return;
      if (Is_ValidResponse(res, dispatch)) {
        const arrList = Array.isArray(res.objEntRpt) ? res.objEntRpt : [];
        setarrLedger(arrList);
        setarrLedgerOption(arrList);
      }
    } catch (e) {
      if (IsMounted.current) Show_Message(dispatch, e.message, "error");
    }
  };
  const Branch_Onchange = (branch) => {
    if (!branch || IsBranchLocked) return; // consolidated statements: the box is locked
    const BranchId = Number(branch.iBranchId);
    if (BranchId === Number(iBranchId)) return; // same branch picked again: nothing to reload
    setiBranchId(BranchId);
    ReqSeq.current++;
    Clear_Data();
    const query = { ...(QueryRef.current || {}), iBranchId: BranchId, iWHId: 0, CurrtPage: 1 };
    QueryRef.current = query;
    if (Number(query.iRptId) === 1015 && IsBranchWiseRef.current) {
      // branch-wise books list ledgers per branch: choose the ledger again
      setstrLedger("");
      QueryRef.current = { ...query, strLedger: "" };
      Load_Ledger(BranchId);
    }
    if (Can_Fetch(QueryRef.current)) Fetch_Report(QueryRef.current);
  };
  return (
    <>
      <SideMenu ScrId={87} />
      <ReportTable
        ReportData={arrReportData}
        ReportDesing={arrReportDesing}
        EntReports={arrReports}
        EntLedger={arrLedger}
        EntLedgerOption={arrLedgerOption}
        Party_OptionShow={(e) => {}}
        View_Report={View_Report}
        Load_Reports={Load_Reports}
        Load_Ledger={() => Load_Ledger()}
        Ledger_Change={Ledger_Change}
        Search_Report={Search_Report}
        EntSum={arrSummarydata}
        iRptId={iRptId}
        strReportName={strReportName}
        FilterColName={strFilterColName}
        setstrFilterColName={(e) => {
          setstrFilterColName(e);
          if (QueryRef.current) QueryRef.current = { ...QueryRef.current, strColName: e };
        }}
        IsAccounts={true}
        strFromDate={strFromDate}
        strToDate={strToDate}
        strMinDate={strBooksFrom}
        IsDateFilter={IsDateFilter}
        Date_Change={Date_Change}
        strLedger={strLedger}
        IsLedOption={IsLedgOption}
        setIsLedOption={(e) => setIsLedgOption(e)}
        setarrLedgerOption={(e) => setarrLedgerOption(e)}
        setstrLedger={(e) => setstrLedger(e)}
        Clear_Report={Clear_Report}
        TotalRows={iTotalRows}
        RowPerPage={RowPerPage}
        View_PageReport={View_PageReport}
        CurrPage={CurrPage}
        IsPaged={IsPaged}
        IsSortable={IsSortable && !Is_StatementReport(iRptId)}
        IsSortLocked={Is_StatementReport(iRptId)}
        IsFilterHidden={Is_StatementReport(iRptId)}
        EntActiveFilters={arrActiveFilters}
        strSortCol={strSortCol}
        strSortOrder={strSortOrder}
        Sort_Report={Sort_Report}
        Reload_Report={Reload_Report}
        Filter_Applied={() => {
          // a filter changed: the count, totals, chips and page are no longer valid -> page 1, full reload
          if (Can_Fetch(QueryRef.current)) Refetch({ CurrtPage: 1 });
        }}
        IsLoading={IsLoading}
        iActiveFilters={iActiveFilters}
        EntAppliedFilters={arrAppliedFilters}
        strFinFromDate={strFinFromDate}
        Download_Report={Download_Report}
        arrWH={arrWH}
        arrBranch={arrBranch}
        iBranchId={iBranchId}
        IsBranchLocked={IsBranchLocked}
        strBranchLockText={BRANCH_LOCK_TEXT}
        Branch_Onchange={Branch_Onchange}
        iWHId={0}
        setiWHId={() => {}}
        IsWHdisable={true}
      />
    </>
  );
}

export default AccountsReport;